/**
 * Reply desk guide: how staff answer Contact and Feedback messages with
 * AI-drafted replies, account actions and threaded customer answers.
 *
 * Static help, inside the admin so every staff account that can open the
 * inboxes can read it (editor accounts never leave Editorial Desk). Keep the
 * button and chip names here in step with ReplyPanel, ReplyActionCards,
 * InboundBlock and lib/replies.ts when those change.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/** A button, chip or field name, set so staff can match it to the screen. */
function K({ children }: { children: ReactNode }) {
  return (
    <span className="font-mono text-[12px] text-paper border border-ink-500 border-b-2 rounded-sm px-1.5 py-px whitespace-nowrap">
      {children}
    </span>
  )
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="space-y-4 scroll-mt-6">
      <h2 id={`${id}-h`} className="font-serif text-[26px] leading-tight text-paper">{title}</h2>
      {children}
    </section>
  )
}

function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto border border-ink-500">
      <table className="w-full text-sm">
        <thead>
          <tr>{head.map((h) => <th key={h} className="label text-left px-3 py-2 border-b border-ink-500">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-ink-500 last:border-b-0 align-top">
              {r.map((c, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? 'text-paper' : 'text-paper-dim'}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Note({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-l-2 border-amber-glow pl-4 py-1 space-y-1 max-w-[70ch]">
      <p className="label-amber">{title}</p>
      <p className="text-sm text-paper-dim">{children}</p>
    </div>
  )
}

const CONTENTS: [string, string][] = [
  ['desk', 'Customer Support desk'], ['glance', 'At a glance'], ['where', 'Where to find it'], ['list', 'Reading the inbox'],
  ['panel', 'The Reply panel'], ['own', 'Writing your own reply'], ['actions', 'Account actions'],
  ['answers', 'When the customer answers'], ['unthreaded', 'Unthreaded answers'], ['issues', 'Known issues'],
  ['practice', 'Good practice'], ['faq', 'Questions and problems'], ['soon', 'Coming soon'],
]

const p = 'text-paper-dim max-w-[68ch]'
const ul = 'list-disc pl-5 space-y-1.5 text-paper-dim max-w-[70ch]'

export function ReplyDeskGuidePage() {
  return (
    <div className="p-8 max-w-[1100px] grid gap-10 lg:grid-cols-[200px_minmax(0,1fr)]">
      <nav aria-label="Contents" className="lg:sticky lg:top-6 self-start space-y-1 text-sm">
        <p className="label mb-2">Contents</p>
        {CONTENTS.map(([id, label]) => (
          <a key={id} href={`#${id}`} className="block text-paper-trace hover:text-amber-glow border-l border-ink-500 pl-3 py-0.5">
            {label}
          </a>
        ))}
      </nav>

      <div className="space-y-12 min-w-0">
        <header className="border-b border-ink-500 pb-6">
          <div className="label mb-1">Members · help</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">Reply desk guide</h1>
          <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
            How to work the Customer Support desk and answer messages from the Contact form, the “Tell us straight”
            feedback sheet and customer email, with drafted replies, account actions and threaded customer answers,
            for all four titles.
          </p>
        </header>

        <Section id="desk" title="Customer Support desk">
          <p className={p}>
            When the desk is switched on, <span className="text-paper">Customer Support</span> brings Contact, Feedback
            and Email together, one workspace per title. The Contact, Feedback, Email and Unthreaded menu items move inside
            it, and Unthreaded is reached from Setup. The sections below that name those menu items apply to the same
            screens under Customer Support.
          </p>
          <p className={p}>
            Today only admins can open the desk. The operator and viewer roles don't have access yet. Widening access
            is an admin and operator decision.
          </p>
          <ul className={ul}>
            <li><span className="text-paper">Counts.</span> The Customer Support menu item shows the total to review, and each title shows its own count. <K>To review</K> means a message nobody has looked at yet and that isn't snoozed. Email that was filtered out doesn't count.</li>
            <li><span className="text-paper">States.</span> <K>To review</K>, <K>Waiting on customer</K> (we replied, or someone marked it reviewed), <K>Snoozed</K> and <K>Done</K>.</li>
            <li><span className="text-paper">Target.</span> Each title has a first-review target, 24 hours unless an admin changes it in Customer Support, Setup. Messages waiting longer than that are flagged.</li>
            <li><span className="text-paper">Owners and actions.</span> Give a conversation an owner from the named staff, or use <K>Assigned to me</K>. <K>Snooze</K> hides it for 1 day, 3 days or 1 week, and it comes back to To review by itself. <K>Mark reviewed</K> moves it to Waiting. <K>Archive</K> closes it.</li>
            <li><span className="text-paper">Auto-close.</span> Every night at 04:10 UTC, a Waiting on customer conversation is archived if our last reply is older than the title's wait window (7 days unless changed in Setup) and the customer hasn't answered since. A snooze pushes this back: the window counts from when the snooze ends, if that is later. A conversation we never replied to is never closed. If the customer answers later, it reopens in To review.</li>
            <li><span className="text-paper">Notes.</span> Internal notes stay with the conversation. They are never sent to the customer.</li>
            <li><span className="text-paper">Saved replies.</span> Insert one into the reply draft at the cursor, then edit it. Creating saved replies and archiving them are admin actions today.</li>
            <li><span className="text-paper">Member panel.</span> Shows who the sender is, their memberships, their earlier conversations and, for staff allowed to see it, their reading activity. Payments appear only for memberships AAMCirc bills (Authorize.Net or Stripe), and Next renewal shows a dash for the others. For PayPal and SimpleCirc it says payments aren't kept here. A <K>Sender not verified</K> badge means don't share account details.</li>
          </ul>
        </Section>

        <Section id="glance" title="At a glance">
          <div className="border border-amber-glow p-4 space-y-1 max-w-[70ch]">
            <p className="text-paper font-semibold">Nothing goes to a customer until you press Send.</p>
            <p className="text-sm text-paper-dim">
              The AI writes drafts and suggests account changes. A person reviews every reply, and every account
              change is applied only when you press <K>Apply and send</K>.
            </p>
          </div>
          <ul className={ul}>
            <li><span className="text-paper">Drafted replies.</span> Each new message gets a draft in the title's voice, built from what AAMCirc knows about the member: memberships, payments, sign-ins and recent problems.</li>
            <li><span className="text-paper">Account actions.</span> A draft can suggest resending a sign-in link, correcting a name or address, or changing an email address. You approve each one.</li>
            <li><span className="text-paper">Customer answers come back.</span> When a customer replies to your email, the answer lands under the original message, the message goes back to Unread, and a fresh draft is prepared.</li>
            <li><span className="text-paper">Attachments.</span> Files customers attach to their answers can be opened from the message, up to 10 MB each.</li>
          </ul>
        </Section>

        <Section id="where" title="Where to find it">
          <Table head={['Menu item', "What's there"]} rows={[
            [<Link to="/contact" className="hover:underline">Contact</Link>, 'With the desk on, this is inside Customer Support. Messages from the Contact form on the member sites, with the Reply panel. The badge counts unread messages.'],
            [<Link to="/feedback" className="hover:underline">Feedback</Link>, '“Tell us straight” feedback. Only feedback that left an email address can be answered.'],
            [<Link to="/unthreaded" className="hover:underline">Unthreaded</Link>, "With the desk on, reach it from Customer Support, Setup. Customer emails that came back but couldn't be matched to a message. Normally empty."],
            [<Link to="/known-issues" className="hover:underline">Known issues</Link>, 'Current problems and recent fixes the drafts take into account. Keep it up to date.'],
          ]} />
        </Section>

        <Section id="list" title="Reading the inbox">
          <p className={p}>
            The list opens on <K>Unread</K>, the work still to do. Use the <K>Status</K> and <K>Title</K> filters to
            see other messages. Each row carries a chip that shows where its reply stands.
          </p>
          <Table head={['Chip', 'What it means', 'What to do']} rows={[
            [<K>Drafting…</K>, 'A draft is being written. This usually takes 1 to 3 minutes.', 'Wait, or write your own reply.'],
            [<K>Draft ready</K>, 'A draft is waiting for review.', 'Open the message, review the draft and send it.'],
            [<K>No reply needed</K>, 'The AI judged no answer is needed, for example a thank-you or a test. The reason is shown in the panel.', 'Check you agree. You can still reply.'],
            [<K>Draft failed</K>, "The draft couldn't be made. The reason is shown in the panel.", <>Press <K>Redraft</K>, or write your own reply.</>],
            [<K>Customer replied</K>, 'The customer answered your last reply.', 'Read their answer and the new draft.'],
            [<K>Replied</K>, 'A reply has been sent and nothing is waiting.', 'Archive it when the matter is closed.'],
            [<K>No email</K>, 'Feedback left without an email address.', "It can't be answered."],
          ]} />
          <p className={p}>
            Sending a reply moves the message from <K>Unread</K> to <K>In progress</K>. Use <K>Mark archived</K> when
            it's done. A customer's answer moves it back to <K>Unread</K>.
          </p>
        </Section>

        <Section id="panel" title="The Reply panel">
          <p className={p}>Open a message to see the Reply panel under it. From top to bottom:</p>
          <ol className="list-decimal pl-5 space-y-3 text-paper-dim max-w-[70ch]">
            <li><span className="text-paper">The conversation.</span> Your earlier replies, with who sent them and how much was changed from the draft, interleaved with the customer's answers, oldest first.</li>
            <li><span className="text-paper">Category and confidence.</span> The topic the AI recognised, such as billing or sign-in, and how sure it is. <em>High</em> means every fact in the draft was checked against AAMCirc's records. Read <em>medium</em> and <em>low</em> drafts more carefully.</li>
            <li><span className="text-paper">Check before sending.</span> A red box appears if the draft promises something, such as “we've refunded you”, that isn't on the staff-action list; says a charge may not be ours or may be from another merchant; quotes a price that isn't in the member's records; overstates the archive (“every issue”); or answers a billing question on points it couldn't verify. Fix each one, or make sure it really is true, before you send.</li>
            <li><span className="text-paper">Staff actions.</span> Things a person must do for the reply to be true, for example “refund the 1 October charge”. Mark each one <K>Done</K> or <K>Not needed</K>. <K>Send</K> stays locked until every one is marked.</li>
            <li><span className="text-paper">Not verified, check these first.</span> Statements the AI couldn't confirm. Check them, or take them out, before sending.</li>
            <li><span className="text-paper">Account actions.</span> Suggested account changes, if any. See <a href="#actions" className="text-amber-glow hover:underline">Account actions</a>.</li>
            <li><span className="text-paper">Subject and reply.</span> Edit freely. The reply goes to the address shown, in the title's design and signed by the title's team, with their message quoted below automatically. Formatting: <code>**bold**</code>, lines starting <code>- </code> for bullets, <code>1. </code> for steps, <code>[link text](https://…)</code>, and at most one button: <code>[button: Label](https://…)</code>.</li>
            <li><span className="text-paper">Buttons.</span> <K>Send</K>, or <K>Apply and send</K> when there are account actions, sends the reply. <K>Preview</K> shows the finished email at <K>Phone</K> and <K>Desktop</K> width. <K>Redraft</K> asks for a new draft. Type an instruction in the box beside it first, such as “shorter, and mention the 3 October fix”. <K>Discard draft</K> throws the draft away and asks for an optional reason.</li>
            <li><span className="text-paper">What I checked.</span> Expand it to see every lookup the AI made and what it found. Useful when a draft surprises you. The detail is cleared after 90 days.</li>
          </ol>
          <Note title="Your edits teach the system">
            AAMCirc records how much you change each draft. Replies sent with few changes become examples for later
            drafts on the same title and topic, so correcting tone and facts improves the drafts over time.
          </Note>
        </Section>

        <Section id="own" title="Writing your own reply">
          <p className={p}>You never have to use the draft. There's no separate button for writing your own:</p>
          <ul className={ul}>
            <li><span className="text-paper">While a draft is being written</span>, type a subject and reply in the empty boxes and press <K>Send</K>.</li>
            <li><span className="text-paper">When a draft is shown</span>, replace its text with your own and send. Or press <K>Discard draft</K> first if you want the record to show the draft wasn't used.</li>
            <li><span className="text-paper">When the panel says “No reply needed”</span>, you can still type a reply and send it, or press <K>Redraft</K> with an instruction such as “reply to confirm we received this”.</li>
          </ul>
          <p className={p}>Staff actions on a draft still need marking before a hand-written reply can be sent. Discarding the draft clears them.</p>
        </Section>

        <Section id="actions" title="Account actions">
          <p className={p}>
            When a member asks for something AAMCirc can do directly, the draft may include <span className="text-paper">Account
            actions</span>. Nothing changes until you press <K>Apply and send</K>. At that point each change is applied
            first, and the reply then describes what actually happened.
          </p>
          <Table head={['Action', 'What it does']} rows={[
            [<K>Resend sign-in link</K>, "Emails a fresh sign-in link to the address on the member's account, never to another address."],
            [<K>Correct name</K>, "Updates the member's first and last name."],
            [<K>Correct postal address</K>, "Updates the member's mailing address."],
            [<K>Change email</K>, 'Changes the email on the account. Usually this sends a confirmation link to the current address, and the change happens only when the member clicks it.'],
          ]} />
          <h3 className="font-serif text-[19px] text-paper">What each action card tells you</h3>
          <Table head={['Label', 'Meaning']} rows={[
            [<K>Will apply now</K>, 'The change will be made when you press Apply and send.'],
            [<K>Will send a confirmation link</K>, 'An email change will wait for the member to confirm from their current address.'],
            [<K>Refused</K>, "AAMCirc won't do it. The reason is shown under the card, for example that the sender couldn't be confirmed as the member."],
          ]} />
          <ul className={ul}>
            <li><K>Skip</K> drops an action without touching the rest of the reply. <K>Keep</K> brings it back.</li>
            <li><K>Edit</K> lets you correct the new name, address or email. <K>Check and keep</K> then re-checks it.</li>
            <li><K>Verified by account number and ZIP</K> appears on email changes. Tick it only if you have personally confirmed the member's account number and ZIP code. It lets the change apply straight away instead of by confirmation link.</li>
            <li>The draft contains markers like <code>{'{{action:1}}'}</code>. Leave them in place: each one is replaced with a sentence describing what actually happened. If you skip an action, delete its marker. The panel tells you if a marker is missing or left over.</li>
            <li>If an action fails when you press Apply and send, nothing is sent. Choose <K>Skip it</K> to send without that action, or <K>Retry</K>.</li>
          </ul>
          <Note title="Sign-in links and email changes need member email to be open for the title">
            Today that's Flight Journal and Model Airplane News. For other titles those two actions are refused with
            “Member email isn't open for this title yet … Do it by hand.” Name and address corrections work for every title.
          </Note>
        </Section>

        <Section id="answers" title="When the customer answers">
          <p className={p}>Each reply you send carries its own return address. When the customer answers it:</p>
          <ul className={ul}>
            <li>Their answer appears under the original message, in the conversation.</li>
            <li>The message goes back to <K>Unread</K> with the <K>Customer replied</K> chip.</li>
            <li>A new draft is written that responds to their latest answer.</li>
            <li>service@airage.com receives a short heads-up, “Customer replied: Contact #… (title)”, with a link to the message. The customer's words aren't in that email. Read and reply here.</li>
          </ul>
          <p className={p}>
            Attached files are listed under the answer. Click a file name to open it. The link works for 10 minutes;
            reload the page for a fresh one. Files over 10 MB show as “over 10 MB, not kept”, so ask the customer for a
            smaller copy if you need it.
          </p>
          <Table head={['You see', 'What it means']} rows={[
            ['“from …, not the address we wrote to”', "The answer came from a different address than the one we replied to. It's kept, but drafts won't share account details and account changes are treated as unconfirmed."],
            ['“automatic reply”', "An out-of-office or similar auto-reply. It doesn't reopen the message or make a new draft."],
            ['Spam', 'Answers flagged as spam are hidden from the conversation.'],
          ]} />
          <Note title="Replies sent before 3 October 2026">
            Those replies pointed at service@airage.com, so answers to them still arrive in that mailbox, not here.
          </Note>
        </Section>

        <Section id="unthreaded" title="Unthreaded answers">
          <p className={p}>
            If an email arrives at one of our reply addresses but can't be matched to a reply we sent, it appears under{' '}
            <Link to="/unthreaded" className="text-amber-glow hover:underline">Unthreaded</Link> instead. This is rare,
            usually a mistyped address. With the desk on, it's reached from Customer Support, Setup. Read it, and answer from the service@ mailbox if it needs a response. The menu
            badge counts these from the last 30 days.
          </p>
        </Section>

        <Section id="issues" title="Known issues">
          <p className={p}>
            The drafter reads the <Link to="/known-issues" className="text-amber-glow hover:underline">Known issues</Link> list
            before every draft. When a problem affects many members, such as the issue viewer on iPhone or a delayed
            renewal email, add it there so drafts explain it correctly and consistently.
          </p>
          <ul className={ul}>
            <li><K>Add issue</K>: a short title, what members see and what we did about it, and the title it affects (or all titles).</li>
            <li><K>Mark fixed today</K>: when it's fixed. Drafts mention the fix for 30 days afterwards.</li>
          </ul>
        </Section>

        <Section id="practice" title="Good practice">
          <ul className={ul}>
            <li>Read the whole draft before sending, including the facts it states about the member.</li>
            <li>Use <K>Preview</K> for anything with links or a button.</li>
            <li>Never tell a customer something was done unless it was. If the reply depends on a refund or a fix, put it on the staff-action list and do it.</li>
            <li>Don't share account details with someone who isn't clearly the member. Drafts already hold back in that case, so don't add them back by hand.</li>
            <li>Prefer <K>Redraft</K> with a clear instruction over rewriting a long draft by hand. It keeps the title's voice.</li>
            <li>Archive messages when the matter is closed, so Unread stays a true to-do list.</li>
          </ul>
        </Section>

        <Section id="faq" title="Questions and problems">
          <dl className="space-y-4 max-w-[70ch]">
            {([
              ['The draft has been “Drafting…” for a long time.', 'Drafts usually take 1 to 3 minutes and are written one at a time, oldest first, so after a busy period there can be a short queue. You can write your own reply meanwhile.'],
              ['“A reply went to this person in the last 10 minutes. Send another?”', <>A guard against double sends. Press <K>Send anyway</K> only if you mean to send a second email.</>],
              ['“Not sent: this address is suppressed …”', "The address has bounced or complained before, so AAMCirc won't email it. Your text is kept. Contact the member another way."],
              ['“Mark each staff action Done or Not needed.”', 'Send is locked until every staff action has been marked.'],
              ['“No email address to reply to.”', "The feedback was left without an address, so it can't be answered."],
              ['The draft got something wrong.', 'Fix it before sending. If the same mistake keeps coming back, add a Known issue or tell the AAMCirc team.'],
            ] as [string, ReactNode][]).map(([q, a]) => (
              <div key={q}>
                <dt className="text-paper">{q}</dt>
                <dd className="text-sm text-paper-dim mt-1">{a}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section id="soon" title="Coming soon: the Email inbox">
          <p className={`${p} border border-dashed border-ink-500 p-4`}>
            Mail sent directly to service@airage.com will also appear here, in an Email inbox that works the same way
            as Contact. Store, vendor and system mail can be kept out with Ignore sender and filter rules, and every
            message stays in the service@ mailbox as well. This guide will be updated when it's switched on.
          </p>
        </Section>

        <p className="text-xs text-paper-trace border-t border-ink-500 pt-4">Last updated 3 October 2026.</p>
      </div>
    </div>
  )
}
